import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { Book } from '@mui/icons-material';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DvrIcon from '@mui/icons-material/Dvr';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LocationCityIcon from '@mui/icons-material/LocationCity';

import { Company, Person, Role, isAdminRole } from '../../models';
import { myLocalStorage } from '../../myLocalStorage';
import { getAcademicMainUrl } from '../../routes/routeUtils';
import {
  AccessCodeIcon,
  AdoptionsIcon,
  AssignmentIcon,
  BillingIcon,
  ConnectionIcon,
  DemosIcon,
  DistributorIcon,
  OnixIcon,
  ProductIcon,
  SchoolIcon,
  SignInIcon,
} from '../Icons';
import { MainLayout } from '../MainLayout';
import { VerticalNavItem } from '../MainLayout/Drawer/types';
import { StudentTab } from '../School/V2/useAcademicNav';
import { UserMenu } from './UserMenu';

export default function LayoutView() {
  const { t } = useTranslation();
  const location = useLocation();

  const pathname = location.pathname;
  const isAccess = pathname.startsWith('/access');
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchPressed, setSearchPressed] = useState(false);
  const [clearSearch, setClearSearch] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const me: Person | undefined = myLocalStorage.getItem(`person`);
  const company: Company | undefined = myLocalStorage.getItem(`company`);

  const getNoLoginItems = (): VerticalNavItem[] => [
    {
      value: 'signIn',
      href: '/signIn',
      selected: location.pathname === '/signIn',
      IconComponent: SignInIcon,
      text: t('signIn'),
    },
  ];

  const getPublisherItems = (): VerticalNavItem[] => {
    const stage = myLocalStorage.getItem('cwst') ?? 'build';

    return [
      {
        value: 'home',
        href: '/',
        selected: location.pathname === '/',
        IconComponent: DashboardIcon,
        text: t('dashboard') ?? 'Dashboard',
        onClick: () => {
          myLocalStorage.setItem('cwst', stage);
        },
      },
      {
        value: 'demos',
        href: '/publisher/demos',
        selected: location.pathname === '/publisher/demos',
        IconComponent: DvrIcon,
        text: t('Product Demos', 'Product Demos'),
        onClick: () => {
          myLocalStorage.setItem('cwst', stage);
        },
      },
      {
        value: 'titleRequests',
        href: '/publisher/titleRequests',
        selected: location.pathname === '/publisher/titleRequests',
        IconComponent: Book,
        text: t('titleRequests'),
        onClick: () => {
          myLocalStorage.setItem('cwst', stage);
        },
      },
      {
        value: 'adoptions',
        href: '/publisher/adoptions',
        selected: location.pathname === '/publisher/adoptions',
        IconComponent: AdoptionsIcon,
        text: t('adoptions'),
        onClick: () => {
          myLocalStorage.setItem('cwst', stage);
        },
      },
      /*,
      {
        value: 'catalog',
        href: '/publisher/catalog',
        selected: location.pathname === '/publisher/catalog',
        IconComponent: ProductIcon,
        text: t('catalog'),
        onClick: () => {
          myLocalStorage.setItem('cwst', stage);
        },
      },*/
    ];
  };

  const getInternalItems = (): VerticalNavItem[] => [
    {
      value: 'home',
      href: '/',
      selected: location.pathname === '/',
      IconComponent: DashboardIcon,
      text: t('dashboard') ?? 'Dashboard',
    },
    {
      value: 'catalog',
      href: '/administrator/catalog',
      selected: location.pathname.startsWith('/administrator/catalog'),
      IconComponent: ProductIcon,
      text: t('catalog') ?? 'Catalog',
    },
    {
      value: 'courses',
      href: '/administrator/courses',
      selected: location.pathname.startsWith('/administrator/courses'),
      IconComponent: SchoolIcon,
      text: t('courses') ?? 'Courses',
    },
    {
      value: 'adoptions',
      href: '/administrator/adoptions',
      selected: location.pathname.startsWith('/administrator/adoptions'),
      IconComponent: CloudSyncIcon,
      text: 'Adoptions',
    },
    {
      value: 'onixJobs',
      href: '/administrator/onixJobs',
      selected: location.pathname === '/administrator/onixJobs',
      IconComponent: OnixIcon,
      text: 'Processed Jobs',
    },
    {
      value: 'connections',
      href: '/administrator/connectionRequests',
      selected: location.pathname === '/administrator/connectionRequests',
      IconComponent: ConnectionIcon,
      text: 'Connections',
    },
    {
      value: 'divider-1',
      isDivider: true,
    },
    {
      value: 'schools',
      href: '/administrator/schools',
      selected: location.pathname === '/administrator/schools',
      IconComponent: LocationCityIcon,
      text: t('schools'),
    },
    {
      value: 'publishers',
      href: '/administrator/publishers',
      selected: location.pathname === '/administrator/publishers',
      IconComponent: AutoStoriesIcon,
      text: t('publishers'),
    },
    {
      value: 'distributors',
      href: '/administrator/distributors',
      selected: location.pathname === '/administrator/distributors',
      IconComponent: DistributorIcon,
      text: t('distributors'),
    },
    {
      value: 'divider-1',
      isDivider: true,
    },
    {
      value: 'people',
      href: '/people',
      selected: location.pathname === '/people',
      IconComponent: EmojiPeopleIcon,
      text: t('people'),
    },
    {
      value: 'billing',
      href: '/billingPeriods',
      selected: location.pathname === '/billingPeriods',
      IconComponent: BillingIcon,
      text: t('billing'),
    },
    {
      value: 'accessCodes',
      href: '/accessCodes',
      selected: location.pathname === '/accessCodes',
      IconComponent: AccessCodeIcon,
      text: t('accessCodes'),
    },
    {
      value: 'identifiers',
      href: '/administrator/identifiers',
      selected: location.pathname === '/administrator/identifiers',
      IconComponent: FingerprintIcon,
      text: 'Courseware ISBNs',
    },
  ];

  const getDistributorItems = (): VerticalNavItem[] => [
    {
      value: 'home',
      href: '/',
      selected: location.pathname === '/',
      IconComponent: DashboardIcon,
      text: t('dashboard') ?? 'Dashboard',
    },
    {
      value: 'catalog',
      href: '/distributor/catalog',
      selected: location.pathname === '/distributor/catalog',
      IconComponent: ProductIcon,
      text: t('catalog'),
    },
    {
      value: 'schools',
      href: '/distributor/schools',
      selected: location.pathname === '/distributor/schools',
      IconComponent: SchoolIcon,
      text: t('schools'),
    },
  ];

  const getSchoolBookstoreItems = (): VerticalNavItem[] => [
    {
      value: 'accessCodes',
      href: '/accessCodes',
      selected: location.pathname === '/accessCodes' && location.search === '',
      IconComponent: AccessCodeIcon,
      text: t('accessCodes'),
    },
  ];

  const getSchoolAcademicItems = (): VerticalNavItem[] => [
    {
      value: 'home',
      href: '/',
      selected: location.pathname === '/',
      IconComponent: DashboardIcon,
      text: t('dashboard') ?? 'Dashboard',
    },
    {
      value: 'assignments',
      href: getAcademicMainUrl(undefined, StudentTab.Assignments),
      selected: false,
      IconComponent: AssignmentIcon,
      text: t('assignments'),
    },
  ];

  const getSchoolItems = (): VerticalNavItem[] => {
    switch (me?.role) {
      case Role.BOOKSTORE_ADMINISTRATOR:
        return getSchoolBookstoreItems();
      case Role.ACADEMIC:
      default:
        return getSchoolAcademicItems();
    }
  };

  const getNavigationItems = (): VerticalNavItem[] => {
    if (!company) {
      return getNoLoginItems();
    }

    switch (company.type) {
      case `INTERNAL`:
        return getInternalItems();
      case `PUBLISHER`:
        return getPublisherItems();
      case `DISTRIBUTOR`:
        return getDistributorItems();
      case `SCHOOL`:
        return getSchoolItems();
      default:
        return [];
    }
  };

  const navigationItems = getNavigationItems();

  return (
    <MainLayout.Provider>
      <MainLayout.Drawer dark={isAdminRole()}>
        <MainLayout.CollapsibleVerticalNav navigationItems={navigationItems} isLoading={false} />
      </MainLayout.Drawer>

      <MainLayout.LayoutContainer>
        <MainLayout.AppBar>
          <UserMenu />
        </MainLayout.AppBar>

        <MainLayout.Main>
          <Outlet
            context={{
              isAccess,
              isSearching,
              setIsSearching,
              searchText,
              setSearchText,
              searchPressed,
              setSearchPressed,
              clearSearch,
              setClearSearch,
              isFullScreen,
              setIsFullScreen,
            }}
          />
        </MainLayout.Main>
      </MainLayout.LayoutContainer>
    </MainLayout.Provider>
  );
}
